import { ComponentType, FC } from "react";
// eslint-disable-next-line lucide-icon-suffix/enforce-icon-suffix
import type { LucideIcon, LucideProps } from "lucide-react-native";
import { cssInterop } from "nativewind";

import { cn } from "@/utils/tailwind";

type IconPropsType = LucideProps & {
  as: LucideIcon | ComponentType<LucideProps>;
  className?: string;
  strokeWidth?: number;
  fill?: string;
};

const IconImpl: FC<IconPropsType> = ({ as: IconComponent, ...props }) => {
  return <IconComponent {...props} />;
};

cssInterop(IconImpl, {
  className: {
    // @ts-expect-error issue with module suffix + LucideIcon type
    target: "style",
    nativeStyleToProp: {
      height: "size",
      width: "size",
    },
  },
});

/**
 * A wrapper component for Lucide icons with Nativewind `className` support via `cssInterop`.
 *
 * This component allows you to render any Lucide icon while applying utility classes
 * using `nativewind`. It avoids the need to wrap or configure each icon individually.
 *
 * @component
 * @example
 * ```tsx
 * import { ArrowRightIcon } from 'lucide-react-native';
 * import { Icon } from '@/registry/components/ui/icon';
 *
 * <Icon as={ArrowRightIcon} className="text-red-500" size={16} />
 * ```
 *
 * @param {LucideIcon} as - The Lucide icon component to render.
 * @param {string} className - Utility classes to style the icon using Nativewind.
 * @param {number} size - Icon size (defaults to 14).
 * @param {...LucideProps} ...props - Additional Lucide icon props passed to the "as" icon.
 */
export const VnIcon: FC<IconPropsType> = ({
  as: IconComponent,
  className,
  size = 16,
  ...props
}) => {
  return (
    <IconImpl
      as={IconComponent}
      className={cn("text-foreground", className)}
      size={size}
      {...props}
    />
  );
};
